import { IonButton, IonCol, IonContent, IonItemDivider, IonLabel, IonList, IonPage, IonRow } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import React from 'react'
import { useTranslation } from 'react-i18next'
import HeaderNav from '../../../components/headerNav/HeaderNav.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import {
  saveFactorsAffectingCare,
  setDispatchFactors,
  setDispatchFactorsPNNV,
  setFactors,
  setResponseFactors,
  setResponseFactorsPNNV,
  setSceneFactors,
  setSceneFactorsPNNV,
  setTransportFactors,
  setTransportFactorsPNNV,
  setTurnAroundFactors,
  setTurnAroundFactorsPNNV,
} from '../store/factorsAffectingCare.actions'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { getFactorsAffectingCareSelector } from '../selectors'
import { getFactorsAffectingCareOptionsSelector } from '../../options/selectors/factorsAffectingCareOptions.selector'
import { IOptions } from '../../../common/common.types'
import Validation from '../../../components/validation/Validation.component'
import { FactorsAffectingCareConstants } from '../store/factorsAffectingCare.constants'

const FactorsAffectingCare: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const factorsAffectingCareState = useSelector(getFactorsAffectingCareSelector)
  const factorsAffectingCareOptionsState = useSelector(getFactorsAffectingCareOptionsSelector)
  const {
    dispatchFactors,
    dispatchFactorsPNNV,
    responseFactors,
    responseFactorsPNNV,
    sceneFactors,
    sceneFactorsPNNV,
    transportFactors,
    transportFactorsPNNV,
    turnAroundFactors,
    turnAroundFactorsPNNV,
    factors,
    showDispatchFactors,
    showFactors,
    showResponseSceneTransportFactors,
    showTurnAroundFactors,
  } = factorsAffectingCareState
  const {
    dispatchFactorsOptions,
    responseFactorsOptions,
    sceneFactorsOptions,
    transportFactorsOptions,
    turnAroundFactorsOptions,
    factorsOptions,
  } = factorsAffectingCareOptionsState

  const onBackClick = () => {
    history.push(TabConstants.CHART_ROUTE)
  }

  const onSaveClick = () => {
    dispatch(saveFactorsAffectingCare())
  }

  const setAllFactorsToNone = () => {
    const getNoneOption = (optionsArray: IOptions, noneId: number) => {
      const noneOption = optionsArray.find((option) => option.id === noneId || option.name === 'None')
      return noneOption ? { ...noneOption, otherComments: null } : null
    }

    if (showDispatchFactors) {
      const noneDispatchFactor = getNoneOption(dispatchFactorsOptions, 105)
      if (noneDispatchFactor) {
        dispatch(setDispatchFactors([noneDispatchFactor]))
      }
    }

    if (showResponseSceneTransportFactors) {
      const noneResponseFactor = getNoneOption(responseFactorsOptions, 16)
      const noneSceneFactor = getNoneOption(sceneFactorsOptions, 16)
      const noneTransportFactor = getNoneOption(transportFactorsOptions, 16)

      if (noneResponseFactor) {
        dispatch(setResponseFactors([noneResponseFactor]))
      }
      if (noneSceneFactor) {
        dispatch(setSceneFactors([noneSceneFactor]))
      }
      if (noneTransportFactor) {
        dispatch(setTransportFactors([noneTransportFactor]))
      }
    }

    if (showTurnAroundFactors) {
      const noneTurnAroundFactor = getNoneOption(turnAroundFactorsOptions, 360)
      if (noneTurnAroundFactor) {
        dispatch(setTurnAroundFactors([noneTurnAroundFactor]))
      }
    }

    if (showFactors) {
      const noneFactor = getNoneOption(factorsOptions, 16)
      if (noneFactor) {
        dispatch(setFactors([noneFactor]))
      }
    }
  }

  const dispatchFactorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='DispatchFactors'
            label={t('factorsAffectingCare.dispatchFactors')}
            listHeader={t('factorsAffectingCare.dispatchFactors')}
            options={dispatchFactorsOptions}
            savedValues={dispatchFactors}
            action={setDispatchFactors}
            savedPNNVValue={dispatchFactorsPNNV}
            pnnvAction={setDispatchFactorsPNNV}
            customInput={{
              textboxEnabledId: 110,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_DISPATCH_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  const responseFactorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='ResponseFactors'
            label={t('factorsAffectingCare.responseFactors')}
            listHeader={t('factorsAffectingCare.responseFactors')}
            options={responseFactorsOptions}
            savedValues={responseFactors}
            action={setResponseFactors}
            savedPNNVValue={responseFactorsPNNV}
            pnnvAction={setResponseFactorsPNNV}
            customInput={{
              textboxEnabledId: 20,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_RESPONSE_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  const sceneFactorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='SceneFactors'
            label={t('factorsAffectingCare.sceneFactors')}
            listHeader={t('factorsAffectingCare.sceneFactors')}
            options={sceneFactorsOptions}
            savedValues={sceneFactors}
            action={setSceneFactors}
            savedPNNVValue={sceneFactorsPNNV}
            pnnvAction={setSceneFactorsPNNV}
            customInput={{
              textboxEnabledId: 20,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_SCENE_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  const transportFactorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='TransportFactors'
            label={t('factorsAffectingCare.transportFactors')}
            listHeader={t('factorsAffectingCare.transportFactors')}
            options={transportFactorsOptions}
            savedValues={transportFactors}
            action={setTransportFactors}
            savedPNNVValue={transportFactorsPNNV}
            pnnvAction={setTransportFactorsPNNV}
            customInput={{
              textboxEnabledId: 20,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_TRANSPORT_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  const turnAroundFactorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='TurnAroundFactors'
            label={t('factorsAffectingCare.turnAroundFactors')}
            listHeader={t('factorsAffectingCare.turnAroundFactors')}
            options={turnAroundFactorsOptions}
            savedValues={turnAroundFactors}
            action={setTurnAroundFactors}
            savedPNNVValue={turnAroundFactorsPNNV}
            pnnvAction={setTurnAroundFactorsPNNV}
            customInput={{
              textboxEnabledId: 365,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_TURN_AROUND_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  const factorsRow = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='Factors'
            label={t('factorsAffectingCare.factors')}
            listHeader={t('factorsAffectingCare.factors')}
            options={factorsOptions}
            savedValues={factors}
            action={setFactors}
            customInput={{
              textboxEnabledId: 20,
              maxlength: 50,
            }}
          />
          <Validation id={FactorsAffectingCareConstants.SET_FACTORS} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
    </>
  )

  return (
    <IonPage id='FactorsAffectingCarePage'>
      <PCRHeader />
      <HeaderNav
        prevOnClick={onBackClick}
        prevLabel={t('common.buttons.Back')}
        isBackButton={true}
        nextLabel={t('common.buttons.Save')}
        nextOnClick={onSaveClick}
        title={t('factorsAffectingCare.factorsAffectingCare')}
      />
      <IonContent color='light'>
        <IonItemDivider color='light' className='ion-padding'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('fullChart.dispatch.DispatchResponseAndOtherFactors')}
          </IonLabel>
        </IonItemDivider>
        <IonList>
          {showDispatchFactors && dispatchFactorsRow}
          {showFactors && factorsRow}
          {showResponseSceneTransportFactors && responseFactorsRow}
          {showResponseSceneTransportFactors && sceneFactorsRow}
          {showResponseSceneTransportFactors && transportFactorsRow}
          {showTurnAroundFactors && turnAroundFactorsRow}
        </IonList>
        <IonRow className='ion-float-right'>
          <IonButton
            data-testid='SetAllFactorsToNone'
            className='transform-none'
            fill='clear'
            color='primary-alt'
            onClick={() => setAllFactorsToNone()}>
            <IonLabel>{t('factorsAffectingCare.setAllFactorsToNone')}</IonLabel>
          </IonButton>
        </IonRow>
      </IonContent>
    </IonPage>
  )
}

export default FactorsAffectingCare
